import React from 'react';
import { useTranslation } from 'react-i18next';
import { DiaryHomeTasks } from '@/components/DiaryHomeTasks';
import { DiaryTopics } from '@/components/DiaryTopics';
import { DiaryDateRow } from '@/components/DiaryTable/DiaryDateRow';
import { DiaryEmptyDay } from '@/components/DiaryTable/DiaryEmptyDay';
import { DiaryLessonCell } from '@/components/DiaryTable/DiaryLessonCell';
import { LessonNumber } from '@/components/DiaryTable/LessonNumber';
import {
  CELL_BORDER,
  DAY_CARD,
  HEADER_CELL,
  LAST_CELL_BORDER,
  TEXT_CELL_PADDING,
} from '@/components/DiaryTable/columns';
import { AttachmentsDisplay } from '@/types/components';
import type { DiaryEntry, DiaryPrintOptions } from '@/types/diary';
import { tv } from '@/utils/tv';

const printColumns = tv({
  base: 'grid',
  variants: {
    isTopicColumnVisible: { true: '', false: '' },
    isHomeworkColumnVisible: { true: '', false: '' },
  },
  compoundVariants: [
    {
      isTopicColumnVisible: true,
      isHomeworkColumnVisible: true,
      class: 'grid-cols-[40px_240px_minmax(0,1fr)_minmax(0,1fr)]',
    },
    {
      isTopicColumnVisible: true,
      isHomeworkColumnVisible: false,
      class: 'grid-cols-[40px_240px_minmax(0,1fr)]',
    },
    {
      isTopicColumnVisible: false,
      isHomeworkColumnVisible: true,
      class: 'grid-cols-[40px_240px_minmax(0,1fr)]',
    },
    {
      isTopicColumnVisible: false,
      isHomeworkColumnVisible: false,
      class: 'grid-cols-[40px_minmax(0,1fr)]',
    },
  ],
});

interface Props {
  entry: DiaryEntry;
  options: DiaryPrintOptions;
}

export const PrintPreviewCard: React.FC<Props> = ({ entry, options }) => {
  const { t } = useTranslation();
  const { isTopicColumnVisible, isHomeworkColumnVisible } = options;
  const hasLessons = entry.lessons.length > 0;
  const isSubjectLastColumn = !isTopicColumnVisible && !isHomeworkColumnVisible;

  return (
    <div className={DAY_CARD}>
      <DiaryDateRow date={entry.date} />

      {!hasLessons && <DiaryEmptyDay />}

      {hasLessons && (
        <div
          className={printColumns({
            isTopicColumnVisible,
            isHomeworkColumnVisible,
          })}
        >
          <div className="h-12 bg-on-primary-surface" />
          <div className={HEADER_CELL}>{t('diary.table.subject')}</div>
          {isTopicColumnVisible && (
            <div className={HEADER_CELL}>{t('diary.table.topic')}</div>
          )}
          {isHomeworkColumnVisible && (
            <div className={HEADER_CELL}>{t('diary.table.homework')}</div>
          )}

          {entry.lessons.map((lesson, index) => (
            <React.Fragment key={`${lesson.className}-${index}`}>
              <div
                className={`flex items-center justify-center ${CELL_BORDER}`}
              >
                <LessonNumber lessonNumber={lesson.lessonNumber} />
              </div>
              <div
                className={`flex ${isSubjectLastColumn ? LAST_CELL_BORDER : CELL_BORDER}`}
              >
                <DiaryLessonCell lesson={lesson} />
              </div>
              {isTopicColumnVisible && (
                <div
                  className={`flex ${isHomeworkColumnVisible ? CELL_BORDER : LAST_CELL_BORDER}`}
                >
                  <DiaryTopics
                    topics={lesson.topics}
                    attachmentsDisplay={AttachmentsDisplay.Expanded}
                    className={TEXT_CELL_PADDING}
                  />
                </div>
              )}
              {isHomeworkColumnVisible && (
                <div className={`flex ${LAST_CELL_BORDER}`}>
                  <DiaryHomeTasks
                    homeTasks={lesson.homeTasks}
                    attachmentsDisplay={AttachmentsDisplay.Expanded}
                    thirdPartyEvents={lesson.thirdPartyEvents}
                    className={TEXT_CELL_PADDING}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};
